import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import logo from '../../assets/finalclipstonedesginlogo-removebg-preview.png';

// Same password rules as signup in Login.js
const validatePassword = (password) => {
  if (password.length < 8)
    return 'Password must be at least 8 characters long.';
  if (!/[A-Z]/.test(password))
    return 'Password must contain at least one uppercase letter (e.g. A–Z).';
  if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password))
    return 'Password must contain at least one special character (e.g. !, @, #, $).';
  return null;
};

/**
 * ResetPassword page.
 *
 * Lands here when a user clicks the "reset your password" link Supabase
 * emails them (triggered from Login.js's forgot-password panel, which calls
 * supabase.auth.resetPasswordForEmail with redirectTo pointing at this page).
 *
 * Supabase's client automatically exchanges the token in the URL for a
 * temporary "recovery" session before this component even renders (default
 * detectSessionInUrl behaviour) — we just need to detect that session is
 * present, then let the user set a new password via supabase.auth.updateUser.
 */
const ResetPassword = () => {
  const navigate = useNavigate();

  const [ready, setReady] = useState(false);
  const [checking, setChecking] = useState(true);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    // Covers the case where the recovery session is already established by
    // the time this component mounts.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setReady(true);
      setChecking(false);
    });

    // Covers the case where Supabase is still processing the link when this
    // component mounts, and fires the event slightly afterwards.
    const { data: listener } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setReady(true);
        setChecking(false);
      }
    });

    // If neither has fired after a few seconds, stop showing the loading
    // state and fall through to the "invalid link" message.
    const timeout = setTimeout(() => setChecking(false), 4000);

    return () => {
      listener?.subscription?.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setFieldErrors({});

    const errors = {};
    const passwordError = validatePassword(newPassword);
    if (passwordError) errors.newPassword = passwordError;

    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your new password.';
    } else if (newPassword !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match.';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (updateError) {
        setError(updateError.message || 'Could not update your password. Please try again.');
        return;
      }

      setSuccess(true);
      // Sign out of the temporary recovery session so the user logs back in
      // fresh with their new password, rather than staying signed in here.
      await supabase.auth.signOut();

      setTimeout(() => {
        navigate('/login');
      }, 2000);
    } catch (unexpectedError) {
      console.error('Unexpected password reset error:', unexpectedError);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-container" style={styles.container}>
      <div className="auth-card" style={styles.card}>
        <div style={styles.logoRow}>
          <img src={logo} alt="Clipstone Miners Welfare Logo" style={styles.logoImage} />
        </div>

        <h2 style={styles.title}>Reset your password</h2>

        {checking && (
          <p style={styles.subtitle}>Checking your reset link...</p>
        )}

        {!checking && !ready && !success && (
          <>
            <div style={styles.errorAlert}>
              This password reset link is invalid or has expired. Please
              request a new one from the login page.
            </div>
            <button
              type="button"
              onClick={() => navigate('/login')}
              style={styles.button}
            >
              Back to login
            </button>
          </>
        )}

        {ready && !success && (
          <form onSubmit={handleSubmit} style={styles.form} noValidate>
            <p style={styles.subtitle}>Enter a new password for your account.</p>

            {error && (
              <div style={styles.errorAlert}>
                <strong>Reset failed:</strong> {error}
              </div>
            )}

            <div style={styles.inputGroup}>
              <label htmlFor="newPassword" style={styles.label}>New password</label>
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter your new password"
                autoComplete="new-password"
                style={fieldErrors.newPassword
                  ? { ...styles.input, ...styles.inputError }
                  : styles.input}
              />
              {fieldErrors.newPassword && (
                <p style={styles.fieldError}>{fieldErrors.newPassword}</p>
              )}
              {!fieldErrors.newPassword && (
                <p style={styles.hint}>
                  Min. 8 characters, one uppercase letter, and one special character (e.g. !@#$).
                </p>
              )}
            </div>

            <div style={styles.inputGroup}>
              <label htmlFor="confirmPassword" style={styles.label}>Confirm new password</label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Re-enter your new password"
                autoComplete="new-password"
                style={fieldErrors.confirmPassword
                  ? { ...styles.input, ...styles.inputError }
                  : styles.input}
              />
              {fieldErrors.confirmPassword && (
                <p style={styles.fieldError}>{fieldErrors.confirmPassword}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              style={loading
                ? { ...styles.button, ...styles.buttonDisabled }
                : styles.button}
            >
              {loading ? 'Updating...' : 'Update password'}
            </button>
          </form>
        )}

        {success && (
          <div style={styles.successAlert}>
            Password updated! Redirecting you to login...
          </div>
        )}
      </div>
    </div>
  );
};

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    padding: '40px 20px',
    backgroundColor: '#f9f8f6',
    background: 'linear-gradient(135deg, #f9f8f6 0%, #e8e6e1 100%)',
    fontFamily: 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif',
  },
  card: {
    background: '#ffffff',
    padding: 'clamp(22px, 5vw, 50px)',
    borderRadius: '16px',
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.1)',
    width: '100%',
    maxWidth: '500px',
    border: '1px solid #e5e5e5',
  },
  logoRow: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '24px',
    backgroundColor: '#1e3f32',
    padding: 'clamp(12px, 4vw, 20px)',
    borderRadius: '12px',
  },
  logoImage: {
    height: 'clamp(72px, 16vw, 120px)',
    width: 'auto',
  },
  title: {
    margin: '0 0 12px 0',
    textAlign: 'center',
    color: '#1e3f32',
    fontSize: '24px',
    fontWeight: '700',
  },
  subtitle: {
    margin: '0 0 24px 0',
    textAlign: 'center',
    color: '#6b7280',
    fontSize: '15px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  inputGroup: {
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    color: '#374151',
    fontSize: '16px',
    fontWeight: '500',
  },
  input: {
    width: '100%',
    padding: '14px 16px',
    border: '1px solid #d1d5db',
    borderRadius: '8px',
    fontSize: '16px',
    boxSizing: 'border-box',
    outline: 'none',
  },
  inputError: {
    border: '1px solid #dc2626',
    backgroundColor: '#fff8f8',
  },
  fieldError: {
    margin: '6px 0 0 0',
    color: '#dc2626',
    fontSize: '13px',
  },
  hint: {
    margin: '6px 0 0 0',
    color: '#6b7280',
    fontSize: '12px',
  },
  button: {
    backgroundColor: '#1e3f32',
    color: '#ffffff',
    padding: '14px 16px',
    border: 'none',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: '600',
    cursor: 'pointer',
    letterSpacing: '0.02em',
    marginTop: '8px',
    width: '100%',
  },
  buttonDisabled: {
    backgroundColor: '#6b7280',
    cursor: 'not-allowed',
  },
  errorAlert: {
    backgroundColor: '#fff0f0',
    color: '#dc2626',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '13px',
    marginBottom: '20px',
    border: '1px solid #fca5a5',
    lineHeight: '1.5',
  },
  successAlert: {
    backgroundColor: '#ecfdf5',
    color: '#059669',
    padding: '10px 14px',
    borderRadius: '6px',
    fontSize: '13px',
    marginBottom: '20px',
    border: '1px solid #6ee7b7',
    textAlign: 'center',
  },
};

export default ResetPassword;